import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ROUTES, setRecPick } from '@/components/gg/chrome'
import { SEED_COSTS, useSeedBalance, InsufficientSeedsError } from '@/hooks/useSeeds'
import { tossSeeds } from '@/lib/seedFx'
import {
  useFeedData, useFeedTuned, useBuildOk, useStartBuild, useDecide, useNudge, usePull, useMorningPull, useFeedParams,
} from '@/hooks/useFeed'
import { useAddToNewsletter, useNewsletterItems, useRemoveFromNewsletter } from '@/hooks/useNewsletterItems'
import { useAlerts, useDismissAlert, alertHeadline } from '@/hooks/useAlerts'
import type { Alert } from '@/components/alerts/types'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { FeedCard, FeedCardBody, BuildingCard, BreakingCard, alertLabel, type CardHandlers } from './FeedCard'
import { FeedSheet, useFeedToast } from './FeedUi'
import { STAGE_PCT, type FeedHeadline } from './feedTypes'
import {
  ageOf, causeOf, plainError, controlOf, dayKey, dayName, hitOf, labelOf, leftOn, noDash, plural,
} from './feedText'

/**
 * Your feed on the phone (Brian's headlines.html v7.1).
 *
 * A vertical list of tall cards, grouped by the day each story came in and
 * kept for seven days. Nothing is decided on the spot: Build writes it in the
 * background (a Writing bar shows it, then it is in Record ＋ and the Library),
 * × takes it out with Undo and Not interested. No pull to refresh; at the end,
 * Pull 5 more · 3 seeds.
 */

interface PullRun { id: number; more: boolean; refresh?: boolean; started: number; ks: number[]; batchId?: string; landedAt?: number; failed?: boolean; done: Set<number> }

const TUNE = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>

/**
 * "Tuned for Bend" on the first line, "+ 1 place · 4 topics · 3 watches" on
 * the second, and the Tune button against both (Brian, Oct 22). It takes the
 * title's row in the phone's top bar: no large "Your feed".
 */
export function TunedLine({ onOpen }: { onOpen: () => void }) {
  const t = useFeedTuned()
  const rest = [t.morePlaces > 0 ? `+ ${plural(t.morePlaces, 'place')}` : '', plural(t.topics, 'topic'), t.watchesPaused ? 'watches paused' : plural(t.watches, 'watch', 'watches')].filter(Boolean).join(' · ')
  return (
    <button type="button" className="feed-tuned feed-tuned--hd" onClick={onOpen} aria-label="Feed controls: tune what shows up here">
      <span className="feed-tuned__l1">{t.ready ? `Tuned for ${t.home ?? 'your town'}` : 'Tune what shows up here'}</span>
      {t.ready && <span className="feed-tuned__l2">{rest}</span>}
      <b className="feed-tuned__b">{TUNE}Tune</b>
    </button>
  )
}

export function PhoneFeed() {
  const navigate = useNavigate()
  const { data, isFetched, isError, refetch } = useFeedData()
  const tuned = useFeedTuned()
  const { data: balance } = useSeedBalance()
  const buildOk = useBuildOk()
  const startBuild = useStartBuild()
  const { pass, undo } = useDecide()
  const nudge = useNudge()
  const pull = usePull()
  const nl = useNewsletterItems()
  const addNl = useAddToNewsletter()
  const removeNl = useRemoveFromNewsletter()
  const toast = useFeedToast()
  const { data: alerts } = useAlerts()
  const dismissAlert = useDismissAlert()
  const qc = useQueryClient()
  const clientId = useEffectiveClientId()
  const [sheet, setSheet] = useState<{ label: string; body: ReactNode } | null>(null)
  const [going, setGoing] = useState<Set<string>>(new Set())
  const [nlBusy, setNlBusy] = useState<Set<string>>(new Set())
  const [run, setRun] = useState<PullRun | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const closeSheet = useCallback(() => setSheet(null), [])

  const available = balance?.total_available ?? 0

  // ---------- the pull: five cards building at once ----------
  const startPull = useCallback((more: boolean, refresh = false) => {
    const id = Date.now()
    const ks = [0, 1, 2, 3, 4].map((i) => (id * (i + 7) * 31) % 997)
    setRun({ id, more, refresh, started: Date.now(), ks, done: new Set() })
    if (more) tossSeeds(SEED_COSTS.reroll)
    pull.mutate({ more, refresh }, {
      onSuccess: (r) => {
        if (!r.count) {
          setRun(null)
          if (more || refresh) toast.show('Nothing new in your feed right now. Try again later; no seeds used.', 3000)
          return
        }
        setRun((cur) => (cur && cur.id === id ? { ...cur, batchId: r.batch_id, landedAt: Date.now() } : cur))
      },
      onError: (e) => {
        setRun((cur) => (cur && cur.id === id ? { ...cur, failed: true } : cur))
        if (e instanceof InsufficientSeedsError) toast.show(<><b>Not enough seeds left for that.</b> This week’s come back Monday.</>, 3600)
        else toast.show(plainError(e, 'That pull did not go through. No seeds were used.'), 3600)
      },
    })
  }, [pull, toast])

  // /app?refresh=mix (Pull now on Topics): today's five again from the new mix, free.
  const { focus, clearFocus, refreshing } = useFeedParams(useCallback(() => {
    startPull(false, true)
    toast.show('Pulling today’s stories from your new mix. They appear as they’re ready.', 2800)
  }, [startPull, toast]))
  useMorningPull(data, isFetched, useCallback(() => startPull(false), [startPull]), refreshing || !!run?.refresh)

  const batch = useMemo(() => (run?.batchId && data ? data.headlines.filter((h) => h.batch_id === run.batchId) : []), [run?.batchId, data])
  const slotDone = useCallback((runId: number, i: number) => {
    setRun((cur) => {
      if (!cur || cur.id !== runId) return cur
      const done = new Set(cur.done); done.add(i)
      return done.size >= 5 ? null : { ...cur, done }
    })
  }, [])

  // ---------- what's in the list ----------
  const inReveal = new Set(run ? batch.filter((_, i) => !run.done.has(i)).map((h) => h.id) : [])
  const visible = useMemo(() => (data?.headlines ?? []).filter((h) => !data!.decided.has(h.id) && !data!.stories.has(h.id)), [data])
  const listed = visible.filter((h) => !inReveal.has(h.id))
  const writing = data ? [...data.stories.values()].filter((s) => s.status === 'developing') : []

  // A story finished writing: say so, with Record.
  const prevWriting = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!data) return
    const now = new Set(writing.map((s) => s.headline_id as string))
    for (const hid of prevWriting.current) {
      const s = data.stories.get(hid)
      if (s && s.status !== 'developing' && !now.has(hid)) {
        toast.show(<>Ready to record: <b>{noDash(s.headline)}</b></>, 4200, [{ label: 'Record', run: () => navigate(`${ROUTES.record}?story=${s.id}`) }])
      }
    }
    prevWriting.current = now
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Build ----------
  const doBuild = useCallback((h: FeedHeadline) => {
    if (available < SEED_COSTS.build_story) {
      toast.show(<><b>Not enough seeds left for a story.</b> This week’s come back Monday; tap the seeds up top for more.</>, 3600)
      return
    }
    setGoing((g) => new Set(g).add(h.id))
    setTimeout(() => {
      startBuild.mutate(h, {
        onError: (e) => {
          setGoing((g) => { const n = new Set(g); n.delete(h.id); return n })
          if (e instanceof InsufficientSeedsError) toast.show(<><b>Not enough seeds left for a story.</b> This week’s come back Monday; tap the seeds up top for more.</>, 3600)
          else toast.show(plainError(e, 'That build did not start. Nothing was charged.'), 3600)
        },
        onSuccess: () => setGoing((g) => { const n = new Set(g); n.delete(h.id); return n }),
      })
    }, 230)
    toast.show(<>Building · {SEED_COSTS.build_story} seeds. <b>Emma is writing it</b>; it’ll be in Record ＋ when it’s ready.</>, 3000)
  }, [available, startBuild, toast])

  const build = useCallback((h: FeedHeadline) => {
    if (!buildOk.ok) {
      setSheet({
        label: 'Build this story?',
        body: (
          <>
            <h3>Build this story?</h3>
            <p className="feed-why">Emma writes the script and caption in your voice. <b>It uses {SEED_COSTS.build_story} of your {available.toLocaleString('en-US')} seeds.</b> Seeds come back every Monday; you’ll only be asked this once.</p>
            <button type="button" className="feed-sbtn feed-sbtn--pri" onClick={() => { closeSheet(); void buildOk.confirm(); doBuild(h) }}>Build · {SEED_COSTS.build_story} seeds</button>
            <button type="button" className="feed-sbtn feed-sbtn--plain" onClick={closeSheet}>Not now</button>
          </>
        ),
      })
      return
    }
    doBuild(h)
  }, [buildOk, available, closeSheet, doBuild])

  // ---------- A breaking alert (v2.5): Build makes its headline, then the feed's own build ----------
  const refreshAlerts = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['alerts', clientId] })
    void qc.invalidateQueries({ queryKey: ['watch-state', clientId] })
  }, [qc, clientId])
  const doBuildAlert = useCallback((a: Alert) => {
    if (available < SEED_COSTS.build_story) {
      toast.show(<><b>Not enough seeds left for a story.</b> This week’s come back Monday; tap the seeds up top for more.</>, 3600)
      return
    }
    const key = `b_${a.id}`
    setGoing((g) => new Set(g).add(key))
    const fail = (e: unknown) => {
      setGoing((g) => { const n = new Set(g); n.delete(key); return n })
      if (e instanceof InsufficientSeedsError) toast.show(<><b>Not enough seeds left for a story.</b> This week’s come back Monday; tap the seeds up top for more.</>, 3600)
      else toast.show(plainError(e, 'That build did not start. Nothing was charged.'), 3600)
    }
    void (async () => {
      try {
        const hid = await alertHeadline(a.id)
        const { data: h, error } = await supabase.from('headlines').select('*').eq('id', hid).single()
        if (error || !h) throw error ?? new Error('That build did not start. Nothing was charged.')
        startBuild.mutate(h as unknown as FeedHeadline, { onError: fail, onSettled: refreshAlerts })
      } catch (e) { fail(e) }
    })()
    toast.show(<>Building · {SEED_COSTS.build_story} seeds. <b>Emma is writing it</b>; it’ll be in Record ＋ when it’s ready.</>, 3000)
  }, [available, startBuild, toast, refreshAlerts])

  const buildAlert = useCallback((a: Alert) => {
    if (!buildOk.ok) {
      setSheet({
        label: 'Build this story?',
        body: (
          <>
            <h3>Build this story?</h3>
            <p className="feed-why">Emma writes the script and caption in your voice. <b>It uses {SEED_COSTS.build_story} of your {available.toLocaleString('en-US')} seeds.</b> Seeds come back every Monday; you’ll only be asked this once.</p>
            <button type="button" className="feed-sbtn feed-sbtn--pri" onClick={() => { closeSheet(); void buildOk.confirm(); doBuildAlert(a) }}>Build · {SEED_COSTS.build_story} seeds</button>
            <button type="button" className="feed-sbtn feed-sbtn--plain" onClick={closeSheet}>Not now</button>
          </>
        ),
      })
      return
    }
    doBuildAlert(a)
  }, [buildOk, available, closeSheet, doBuildAlert])

  /** Fewer like this on an alert: a watch brings these, so name it; a nudge can't override it. */
  const fewerWatch = useCallback((a: Alert) => {
    setSheet({
      label: 'Fewer like this',
      body: <>
        <h3>Fewer like this</h3><p className="feed-why">Here’s why it was in your list.</p>
        <div className="feed-cause"><span className="feed-cause__k">It came from your watch</span>{a.subject || alertLabel(a)}{a.condition ? ` · ${a.condition}` : ''}</div>
        <p className="feed-why">A watch you set brings these, so they keep coming until you change it. <b>Fewer like this can’t override a watch.</b></p>
        <button type="button" className="feed-sbtn" onClick={() => { closeSheet(); navigate(ROUTES.breaking) }}>Change your watches ›</button>
        <button type="button" className="feed-sbtn feed-sbtn--pri" onClick={closeSheet}>Got it · just hide this one</button>
      </>,
    })
  }, [closeSheet, navigate])

  const removeAlert = useCallback((a: Alert) => {
    const key = `b_${a.id}`
    setGoing((g) => new Set(g).add(key))
    setTimeout(() => {
      dismissAlert.mutate(a.id, { onSettled: () => setGoing((g) => { const n = new Set(g); n.delete(key); return n }) })
      toast.show('Removed.', 5200, [
        { label: 'Undo', run: () => { void supabase.rpc('alert_undismiss' as never, { p_id: a.id } as never).then(refreshAlerts) } },
        { label: 'Not interested', run: () => fewerWatch(a) },
      ])
    }, 230)
  }, [dismissAlert, toast, refreshAlerts, fewerWatch])

  // ---------- Why this story ----------
  const shareOf = useCallback((topic: string | null) => tuned.mix.find((m) => m.topic === topic)?.share ?? 0, [tuned.mix])
  const why = useCallback((h: FeedHeadline) => {
    const c = causeOf(h), ctl = controlOf(h), share = shareOf(h.topic), n = h.source_count ?? 0
    const cause = c === 'record' ? `Your public records check found it${h.source_name ? ` (${h.source_name})` : ''}`
      : c === 'keyword' ? `It names your keyword “${hitOf(h)}”, so it went to the top`
        : `${labelOf(h)} is ${share ? `${share}% of` : 'in'} your topic mix`
    setSheet({
      label: 'Why this story',
      body: (
        <>
          <h3>Why this story</h3>
          <div className="feed-cause"><span className="feed-cause__k">What brought it</span>{cause}</div>
          <div className="feed-facts">
            <span>Topic</span><b>{labelOf(h)}</b>
            <span>Source</span><b>{h.source_name ?? ''}{n > 1 ? ` · ${n} sources carry it` : ''}</b>
            <span>Place</span><b>{h.place || tuned.home || ''}</b>
            <span>Reported</span><b>{ageOf(h)}</b>
          </div>
          <div className="feed-opts">
            <button type="button" className="feed-sbtn" onClick={() => { closeSheet(); navigate(ctl.to) }}>Adjust {ctl.label} ›</button>
            <button type="button" className="feed-sbtn" onClick={() => { closeSheet(); navigate(ROUTES.feedControls) }}>All feed controls ›</button>
          </div>
          <button type="button" className="feed-sbtn feed-sbtn--pri" onClick={closeSheet}>Done</button>
        </>
      ),
    })
  }, [closeSheet, navigate, shareOf, tuned.home])

  // ---------- Fewer like this (name the cause first; only ever a gentle nudge) ----------
  const doNudge = useCallback((h: FeedHeadline, kind: 'topic' | 'source') => {
    const key = kind === 'topic' ? (h.topic ?? '') : (h.source_name ?? '')
    const label = kind === 'topic' ? `${labelOf(h)} stories` : `stories from ${h.source_name}`
    nudge.mutate({ kind, key, label })
    return label
  }, [nudge])

  const fewer = useCallback((h: FeedHeadline) => {
    const c = causeOf(h), share = shareOf(h.topic)
    const go = (to: string, label: string) => <button type="button" className="feed-sbtn" onClick={() => { closeSheet(); navigate(to) }}>{label} ›</button>
    let body: ReactNode
    if (c === 'record') {
      body = <>
        <div className="feed-cause"><span className="feed-cause__k">It came from your public records check</span>{h.source_name ?? ''}</div>
        <p className="feed-why">Records come from the sources you turned on. <b>Turn this source off</b> and they stop.</p>
        {go(ROUTES.recordsSettings, 'Change your records sources')}
      </>
    } else if (c === 'keyword') {
      body = <>
        <div className="feed-cause"><span className="feed-cause__k">It names your keyword</span>“{hitOf(h)}” in {labelOf(h)}</div>
        <p className="feed-why">Your keywords put these at the top, so they keep coming until you change the keyword. <b>Fewer like this can’t override a keyword.</b></p>
        {go(ROUTES.topics, 'Change your keywords')}
      </>
    } else {
      body = <>
        <div className="feed-cause"><span className="feed-cause__k">It came from your topic mix</span>{labelOf(h)}{share ? ` · ${share}% of your stories` : ''}{h.source_name ? ` · from ${h.source_name}` : ''}</div>
        <p className="feed-why">Pick what you want less of. It’s a nudge, not a block: you’ll still see some, so nothing you care about disappears.</p>
        <div className="feed-opts">
          <button type="button" className="feed-sbtn" onClick={() => { const l = doNudge(h, 'topic'); closeSheet(); toast.show(<>A few fewer {l}. <b>Undo it in Topics.</b></>, 3200) }}>A few fewer {labelOf(h)} stories</button>
          {h.source_name && <button type="button" className="feed-sbtn" onClick={() => { const l = doNudge(h, 'source'); closeSheet(); toast.show(<>A few fewer {l}. <b>Undo it in Topics.</b></>, 3200) }}>A few fewer from {h.source_name}</button>}
          <button type="button" className="feed-sbtn" onClick={() => { closeSheet(); navigate(`${ROUTES.topics}#fewer`) }}>See what you’ve asked for less of ›</button>
        </div>
        <p className="feed-fine">For a bigger change, set {labelOf(h)}’s share in Topics. You can undo a nudge there too.</p>
      </>
    }
    setSheet({
      label: 'Fewer like this',
      body: <><h3>Fewer like this</h3><p className="feed-why">Here’s why it was in your list.</p>{body}<button type="button" className="feed-sbtn feed-sbtn--pri" onClick={closeSheet}>Got it · just hide this one</button></>,
    })
  }, [closeSheet, doNudge, navigate, shareOf, toast])

  const notInterested = useCallback((h: FeedHeadline) => {
    if (causeOf(h) !== 'mix') { fewer(h); return }
    const label = doNudge(h, 'topic')
    toast.show(<>Got it: a few fewer {label}.</>, 4200, [{ label: 'More options', run: () => fewer(h) }])
  }, [doNudge, fewer, toast])

  // ---------- × ----------
  const remove = useCallback((h: FeedHeadline) => {
    setGoing((g) => new Set(g).add(h.id))
    setTimeout(() => {
      pass.mutate([h.id])
      setGoing((g) => { const n = new Set(g); n.delete(h.id); return n })
      toast.show('Removed.', 5200, [
        { label: 'Undo', run: () => undo.mutate(h.id) },
        { label: 'Not interested', run: () => notInterested(h) },
      ])
    }, 230)
  }, [pass, undo, notInterested, toast])

  // ---------- ＋ Newsletter (the Library's newsletter_items, newsletter-version) ----------
  const newsletter = useCallback((h: FeedHeadline) => {
    if (nlBusy.has(h.id)) return
    const item = nl.find({ headlineId: h.id })
    if (item) {
      removeNl.mutate(item.id, { onSuccess: () => toast.show('Taken out of this week’s newsletter.') })
      return
    }
    setNlBusy((s) => new Set(s).add(h.id))
    addNl.mutate({ headlineId: h.id }, {
      onSettled: () => setNlBusy((s) => { const n = new Set(s); n.delete(h.id); return n }),
      onSuccess: (r) => {
        const n = r.count ?? nl.items.length + 1
        toast.show(`In this week’s newsletter · ${plural(n, 'story', 'stories')}.`, 3600, [{ label: 'See it', run: () => navigate(ROUTES.newsletter) }])
      },
      onError: (e) => {
        if (e.status === 402 || /insufficient seeds/i.test(e.message)) toast.show(<><b>Not enough seeds left for that.</b> This week’s come back Monday.</>, 3400)
        else toast.show(plainError(e, 'That didn’t go through. Nothing was charged.'), 3400)
      },
    })
  }, [nl, nlBusy, addNl, removeNl, toast, navigate])

  // ---------- a tap on the card: the story, with Build waiting (Brian v5.1: article.html?from=feed) ----------
  const open = useCallback((h: FeedHeadline) => {
    navigate(`${ROUTES.story(h.id)}?from=feed`)
  }, [navigate])

  // ---------- /app?focus=<headlineId> (Emma's offers): bring that card up ----------
  useEffect(() => {
    if (!focus || !data) return
    const listedHere = visible.some((h) => h.id === focus)
    if (!listedHere) {
      // Built, removed or gone from the list: open it (a built one lands on its story).
      clearFocus()
      if (data.headlines.some((h) => h.id === focus)) navigate(`${ROUTES.story(focus)}?from=feed`)
      return
    }
    const t = window.setTimeout(() => {
      const el = listRef.current?.querySelector<HTMLElement>(`.feed-card[data-id="${CSS.escape(focus)}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        el.classList.add('is-focus')
        window.setTimeout(() => el.classList.remove('is-focus'), 2400)
      }
      clearFocus()
    }, 120)
    return () => window.clearTimeout(t)
  }, [focus, data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlers: CardHandlers = { onBuild: build, onRemove: remove, onWhy: why, onNewsletter: newsletter, onOpen: open }

  // ---------- Pull 5 more ----------
  const pullMore = () => {
    if (run) return
    if (available < SEED_COSTS.reroll) { toast.show(<><b>Not enough seeds left for that.</b> This week’s come back Monday.</>, 3600); return }
    startPull(true)
    toast.show('Building 5 more for Today. They appear as they’re ready.', 2600)
    setTimeout(() => listRef.current?.querySelector('.feed-skel')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60)
  }

  // ---------- drawing ----------
  const today = dayKey(Date.now())
  // An alert that broke today sits at the top of Today (decided ones live in Alerts).
  const breaking = (alerts ?? []).filter((a) => a.status === 'new' && dayKey(a.delivered_at) === today)
    .sort((a, b) => a.delivered_at.localeCompare(b.delivered_at))
  const days = new Map<number, FeedHeadline[]>()
  for (const h of listed) { const d = dayKey(h.fetched_at); days.set(d, [...(days.get(d) ?? []), h]) }
  if ((run || breaking.length) && !days.has(today)) days.set(today, [])
  const order = [...days.keys()].sort((a, b) => b - a)
  const total = visible.length + breaking.length
  const pct = writing.length ? Math.round(writing.reduce((n, s) => n + STAGE_PCT[s.build_stage ?? 'queued'], 0) / writing.length) : 0

  const slots = run ? [0, 1, 2, 3, 4].filter((i) => !run.done.has(i)).map((i) => {
    const h = run.landedAt ? batch[i] ?? null : null
    return (
      <BuildingCard key={`${run.id}-${i}`} slot={i} k={run.ks[i]} started={run.started} h={h}
        landedAt={run.landedAt ?? null} failed={run.failed} onDone={() => slotDone(run.id, i)}>
        {h && <FeedCardBody h={h} inNewsletter={false} nlBusy={false} />}
      </BuildingCard>
    )
  }) : null

  return (
    <div className="feed" ref={listRef}>
      {writing.length > 0 && (
        <button type="button" className="feed-wbar" onClick={() => setRecPick(true)}>
          <span className="feed-wbar__t">
            <b>Writing {writing.length === 1 ? `“${noDash(writing[0].headline)}”` : `${writing.length} stories`}</b>
            <small>Emma is writing your script · then it’s in Record ＋</small>
            <span className="feed-wbar__bar"><i style={{ width: `${pct}%` }} /></span>
          </span>
          <span className="feed-wbar__go">{pct}%</span>
        </button>
      )}

      {isError && (
        <div className="feed-end"><h3>Your feed didn’t load</h3><p>Check your connection and try again.</p><button type="button" onClick={() => void refetch()}>Try again</button></div>
      )}

      {order.map((d) => {
        const list = (days.get(d) ?? []).slice().sort((a, b) => a.fetched_at.localeCompare(b.fetched_at))
        const n = list.length + (d === today ? (slots ? slots.length : 0) + breaking.length : 0)
        return (
          <section key={d} className="feed-dayblock">
            <div className="feed-day"><span>{dayName(new Date(d).toISOString())}</span><small>{plural(n, 'story', 'stories')}{d < today ? ` · ${leftOn(new Date(d).toISOString())}` : ''}</small></div>
            {d === today && breaking.map((a) => (
              <BreakingCard key={a.id} a={a} going={going.has(`b_${a.id}`)} onBuild={buildAlert} onRemove={removeAlert}
                onOpen={(x) => navigate(ROUTES.alert(x.id))} />
            ))}
            {list.map((h) => (
              <FeedCard key={h.id} h={h} handlers={handlers} going={going.has(h.id)}
                inNewsletter={nl.has({ headlineId: h.id })} nlBusy={nlBusy.has(h.id)} />
            ))}
            {d === today && slots}
          </section>
        )
      })}

      {isFetched && !isError && (
        <div className="feed-end">
          <h3>{total ? 'That’s everything for now' : 'You’re all caught up'}</h3>
          <p>{total ? 'Stories you haven’t built or removed stay here for a week.' : 'Tomorrow’s stories land at 6am.'} Want more now?</p>
          <button type="button" onClick={pullMore} disabled={!!run}>Pull 5 more<small>· {SEED_COSTS.reroll} seeds</small></button>
          <Link className="feed-tunel" to={ROUTES.feedControls}>Want different stories? Tune your feed ›</Link>
        </div>
      )}

      <FeedSheet open={!!sheet} onClose={closeSheet} label={sheet?.label ?? ''}>{sheet?.body}</FeedSheet>
      {toast.node}
    </div>
  )
}
